import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Handshake, Search } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Cliente } from '@/shared/api/tipos';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { Modal } from '@/shared/ui/Modal';
import { CampoDeMes } from '@/shared/ui/CampoDeMes';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import {
  CONCEPTOS,
  enDolares,
  enFecha,
  type Convenible,
  type Convenio,
  type CuotaSimulada,
  type Garantia,
  type Servicio,
  GARANTIAS,
} from './contrato';

export function ModalDeConvenio({
  alCerrar,
  alRegistrar,
}: {
  alCerrar: () => void;
  alRegistrar: (c: Convenio) => void;
}) {
  const clienteDeConsultas = useQueryClient();
  const [busqueda, setBusqueda] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [elegidas, setElegidas] = useState<Set<string>>(new Set());
  const servicio: Servicio = 'SERVICIOS_ESPECIALES';
  const [deuda, setDeuda] = useState('');
  const [entrada, setEntrada] = useState('');
  const [plazo, setPlazo] = useState('12');
  const [dia, setDia] = useState('6');
  const [garantia, setGarantia] = useState<Garantia>('LETRA_DE_CAMBIO');
  const [redondear, setRedondear] = useState(true);
  const [primerMes, setPrimerMes] = useState(mesSiguiente());
  const [referencia, setReferencia] = useState('');
  const [observacion, setObservacion] = useState('');
  const [representante, setRepresentante] = useState('');
  const [concepto, setConcepto] = useState('');
  const [fechaSolicitud, setFechaSolicitud] = useState('');
  const [sumilla, setSumilla] = useState('');
  const buscado = useValorDiferido(busqueda.trim());

  const clientes = useQuery({
    queryKey: ['clientes', 'convenio', buscado],
    queryFn: () => api.get<Pagina<Cliente>>('/clientes', { query: { texto: buscado, tamanio: 8 } }),
    enabled: cliente === null && buscado.length >= 3,
  });

  const convenible = useQuery({
    queryKey: ['convenios', 'convenible', cliente?.id],
    queryFn: () => api.get<Convenible>(`/convenios/convenible/${cliente!.id}`),
    enabled: cliente !== null,
  });

  const facturas = convenible.data?.facturas ?? [];
  const marcadas = facturas.filter((f) => elegidas.has(clave(f.id, f.historica)));
  const capitalElegido = marcadas.reduce((n, f) => n + f.saldo, 0);
  const moraElegida = marcadas.reduce((n, f) => n + f.interes, 0);
  const sumaElegidas = capitalElegido + moraElegida;
  const deudaNum = Number(deuda || 0);
  const entradaNum = Number(entrada || 0);
  const porcentajeMinimo = convenible.data?.entradaMinimaPorcentaje ?? 20;
  const plazoMaximo = convenible.data?.plazoMaximo ?? 24;
  const publica = convenible.data?.institucionPublica === true;
  const incumplidos = convenible.data?.incumplidos ?? 0;
  const aCoactiva = incumplidos >= 2;
  const entradaMinima = Math.round(deudaNum * porcentajeMinimo) / 100;
  const entradaCorta = deudaNum > 0 && entradaNum < entradaMinima;
  const plazoLargo = Number(plazo) > plazoMaximo;
  const valido =
    cliente !== null &&
    !publica &&
    !aCoactiva &&
    deudaNum > 0 &&
    entradaNum >= 0 &&
    !entradaCorta &&
    entradaNum < deudaNum &&
    Number(plazo) >= 1 &&
    !plazoLargo &&
    Number(dia) >= 1 &&
    Number(dia) <= 31;

  const simulacion = useQuery({
    queryKey: ['convenios', 'simulacion', deudaNum, entradaNum, plazo, primerMes, dia, redondear],
    queryFn: () =>
      api.get<CuotaSimulada[]>('/convenios/simulacion', {
        query: {
          deuda: deudaNum,
          entrada: entradaNum,
          plazo,
          primerMes: `${primerMes}-01`,
          diaDePago: dia,
          redondear,
        },
      }),
    enabled: valido,
  });

  const registrar = useMutation({
    mutationFn: () =>
      api.post<Convenio>('/convenios', {
        clienteId: cliente!.id,
        servicio,
        deuda: deudaNum,
        entrada: entradaNum,
        plazo: Number(plazo),
        diaDePago: Number(dia),
        primerMes: `${primerMes}-01`,
        redondear,
        garantia,
        referencia,
        observacion,
        representante,
        concepto,
        fechaSolicitud: fechaSolicitud || null,
        sumilla,
        facturas: facturas
          .filter((f) => elegidas.has(clave(f.id, f.historica)))
          .map((f) => ({ id: f.id, historica: f.historica })),
      }),
    onSuccess: (c) => {
      notificar.exito(`${c.cliente} · ${c.plazo} cuotas`, 'Registrado: queda por aprobar');
      void clienteDeConsultas.invalidateQueries({ queryKey: ['convenios'] });
      alRegistrar(c);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO REGISTRAR'),
  });

  function alternar(id: number, historica: boolean) {
    const nuevas = new Set(elegidas);
    const k = clave(id, historica);
    if (nuevas.has(k)) {
      nuevas.delete(k);
    } else {
      nuevas.add(k);
    }
    setElegidas(nuevas);
    const suma = facturas
      .filter((f) => nuevas.has(clave(f.id, f.historica)))
      .reduce((n, f) => n + f.total, 0);
    setDeuda(suma > 0 ? suma.toFixed(2) : '');
    setEntrada(suma > 0 ? (Math.round(suma * porcentajeMinimo) / 100).toFixed(2) : '');
  }

  const totalCuotas = useMemo(
    () => (simulacion.data ?? []).reduce((n, q) => n + q.valor, 0),
    [simulacion.data],
  );

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Nuevo convenio"
      subtitulo="La deuda pactada por cuotas"
      icono={Handshake}
      ancho="pantalla"
      cerrarAlPulsarFuera={false}
    >
      {cliente === null ? (
        <div className="grid gap-3">
          <label className="relative block">
            <Search className="absolute left-3 top-3 size-4 text-armazon-400" aria-hidden />
            <input
              autoFocus
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, cédula o RUC del contribuyente…"
              className="h-10 w-full rounded-lg border border-armazon-200 pl-9 pr-3 text-sm"
            />
          </label>
          {buscado.length >= 3 &&
            (clientes.isPending ? (
              <CargandoEnLinea detalle="Buscando" />
            ) : (
              <ul className="divide-y divide-armazon-100 rounded-xl border border-armazon-200">
                {(clientes.data?.contenido ?? []).map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setCliente(c)}
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-marca-50"
                    >
                      <span className="text-xs font-semibold uppercase text-armazon-800">
                        {c.nombre}
                      </span>
                      <span className="importe text-[11px] text-armazon-400">
                        {c.identificacion}
                      </span>
                    </button>
                  </li>
                ))}
                {clientes.data?.contenido.length === 0 && (
                  <li className="px-4 py-6 text-center text-xs uppercase text-armazon-400">
                    Ningún contribuyente coincide
                  </li>
                )}
              </ul>
            ))}
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-armazon-200 bg-armazon-50 px-4 py-3">
              <span>
                <span className="block text-xs font-bold uppercase text-armazon-900">
                  {cliente.nombre}
                </span>
                <span className="importe text-[11px] text-armazon-400">
                  {cliente.identificacion}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setCliente(null);
                  setElegidas(new Set());
                  setDeuda('');
                  setEntrada('');
                }}
                className="text-[11px] font-semibold uppercase text-marca-700 hover:underline"
              >
                Cambiar
              </button>
            </div>

            {publica && (
              <p
                role="alert"
                className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold uppercase text-rose-800"
              >
                Es una institución pública: según Tesorería, las instituciones públicas no firman
                convenios de pago.
              </p>
            )}
            {aCoactiva && (
              <p
                role="alert"
                className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-semibold uppercase text-rose-800"
              >
                Ya incumplió {incumplidos} convenios: no se firma otro, la deuda va a coactiva.
              </p>
            )}
            {incumplidos === 1 && (
              <p
                role="status"
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold uppercase text-amber-900"
              >
                Ya incumplió un convenio: este sería el segundo. Si también lo incumple, va a
                coactiva.
              </p>
            )}

            <section>
              <h3 className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase text-armazon-600">
                <span>Facturas que recoge · con su mora a hoy</span>
                <span className="importe text-armazon-500">
                  {enDolares(capitalElegido)} + {enDolares(moraElegida)} ={' '}
                  <b className="text-armazon-800">{enDolares(sumaElegidas)}</b>
                </span>
              </h3>
              {convenible.isPending ? (
                <CargandoEnLinea detalle="Trayendo lo que debe" />
              ) : facturas.length === 0 ? (
                <p className="rounded-lg border border-armazon-200 p-3 text-center text-[11px] uppercase text-armazon-400">
                  No debe servicios especiales fuera de otro convenio
                </p>
              ) : (
                <ul className="max-h-64 divide-y divide-armazon-100 overflow-y-auto rounded-xl border border-armazon-200">
                  {facturas.map((f) => (
                    <li key={clave(f.id, f.historica)}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-1.5 text-xs hover:bg-armazon-50">
                        <input
                          type="checkbox"
                          checked={elegidas.has(clave(f.id, f.historica))}
                          onChange={() => alternar(f.id, f.historica)}
                        />
                        <span className="importe min-w-0 flex-1 truncate text-armazon-700">
                          {f.numero ?? 'Sin número'}
                          <span className="text-[10px] text-armazon-400"> · {f.periodo}</span>
                          {f.historica && (
                            <span className="ml-1 text-[9px] uppercase text-armazon-400">
                              · sistema anterior
                            </span>
                          )}
                        </span>
                        <span className="importe w-20 text-right text-armazon-500">
                          {enDolares(f.saldo)}
                        </span>
                        <span
                          className="importe w-20 text-right text-amber-700"
                          title={`${f.mesesDeMora} meses de mora`}
                        >
                          {f.interes > 0 ? `+${enDolares(f.interes)}` : '—'}
                        </span>
                        <span className="importe w-20 text-right font-semibold text-armazon-800">
                          {enDolares(f.total)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="grid grid-cols-2 gap-3">
              <Campo etiqueta="De qué es la deuda">
                <span
                  className={`${CAMPO} flex items-center bg-armazon-50 text-[11px] uppercase text-armazon-600`}
                  title="La tasa mensual y los títulos del Art. 8 no van a convenio"
                >
                  Servicios especiales y cartera del SIRE
                </span>
              </Campo>
              <Campo etiqueta="Deuda convenida ($)">
                <input
                  value={deuda}
                  onChange={(e) => setDeuda(e.target.value)}
                  inputMode="decimal"
                  className={CAMPO}
                />
              </Campo>
              <Campo
                etiqueta={`Entrada ($) · mínimo ${porcentajeMinimo} %${deudaNum > 0 ? ` = ${enDolares(entradaMinima)}` : ''}`}
              >
                <input
                  value={entrada}
                  onChange={(e) => setEntrada(e.target.value)}
                  inputMode="decimal"
                  aria-invalid={entradaCorta}
                  className={`${CAMPO} ${entradaCorta ? 'border-rose-400' : ''}`}
                />
              </Campo>
              <Campo etiqueta={`Cuotas · hasta ${plazoMaximo}`}>
                <input
                  value={plazo}
                  onChange={(e) => setPlazo(e.target.value)}
                  inputMode="numeric"
                  aria-invalid={plazoLargo}
                  className={`${CAMPO} ${plazoLargo ? 'border-rose-400' : ''}`}
                />
              </Campo>
              <Campo etiqueta="Día de pago">
                <input
                  value={dia}
                  onChange={(e) => setDia(e.target.value)}
                  inputMode="numeric"
                  className={CAMPO}
                />
              </Campo>
              <Campo etiqueta="Garantía">
                <select
                  value={garantia}
                  onChange={(e) => setGarantia(e.target.value as Garantia)}
                  className={`${CAMPO} uppercase`}
                >
                  {GARANTIAS.map((g) => (
                    <option key={g.valor} value={g.valor}>
                      {g.etiqueta}
                    </option>
                  ))}
                </select>
              </Campo>
              <label className="flex items-end gap-2 pb-2 text-[11px] font-semibold uppercase text-armazon-600">
                <input
                  type="checkbox"
                  checked={redondear}
                  onChange={(e) => setRedondear(e.target.checked)}
                />
                Cuota redondeada al dólar
              </label>
              <Campo etiqueta="Primera cuota en">
                <CampoDeMes
                  valor={primerMes}
                  alCambiar={setPrimerMes}
                  minimo={mesActual()}
                  accion="Primera cuota en"
                  arriba
                />
              </Campo>
              <Campo etiqueta="Respaldo (resolución, oficio)">
                <input
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  className={`${CAMPO} uppercase`}
                />
              </Campo>
              <Campo etiqueta="Observación">
                <input
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                  className={`${CAMPO} uppercase`}
                />
              </Campo>
            </div>

            <section className="grid grid-cols-2 gap-3 rounded-xl border border-armazon-200 p-3">
              <h3 className="col-span-2 text-[11px] font-bold uppercase text-armazon-600">
                Para el documento · como lo pidió
              </h3>
              <Campo etiqueta="Representante legal (si es sociedad)">
                <input
                  value={representante}
                  onChange={(e) => setRepresentante(e.target.value)}
                  className={`${CAMPO} uppercase`}
                />
              </Campo>
              <Campo etiqueta="Fecha del oficio de solicitud">
                <input
                  type="date"
                  value={fechaSolicitud}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFechaSolicitud(e.target.value)}
                  className={CAMPO}
                />
              </Campo>
              <div className="col-span-2">
                <Campo etiqueta="Por concepto de">
                  <input
                    value={concepto}
                    onChange={(e) => setConcepto(e.target.value)}
                    placeholder={CONCEPTOS[servicio]}
                    className={CAMPO}
                  />
                </Campo>
              </div>
              <div className="col-span-2">
                <Campo etiqueta="Sumilla de la gerencia en el oficio">
                  <textarea
                    value={sumilla}
                    onChange={(e) => setSumilla(e.target.value)}
                    rows={2}
                    maxLength={500}
                    className="w-full rounded-lg border border-armazon-200 px-2 py-1.5 text-xs"
                  />
                </Campo>
              </div>
            </section>
          </div>

          <div className="flex flex-col">
            <h3 className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase text-armazon-600">
              <span>Calendario que saldría</span>
              {valido && (
                <span className="importe text-armazon-500">{enDolares(totalCuotas)} en cuotas</span>
              )}
            </h3>
            {!valido ? (
              <p className="rounded-xl border border-dashed border-armazon-300 p-6 text-center text-[11px] uppercase text-armazon-400">
                Ponga la deuda, la entrada y el plazo para ver las cuotas
              </p>
            ) : simulacion.isPending ? (
              <CargandoEnLinea detalle="Calculando" />
            ) : (
              <div className="max-h-96 overflow-y-auto rounded-xl border border-armazon-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-armazon-50 text-[10px] uppercase text-armazon-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Nº</th>
                      <th className="px-3 py-2 text-left">Vence</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-armazon-100">
                    {(simulacion.data ?? []).map((q) => (
                      <tr key={q.numero}>
                        <td className="importe px-3 py-1.5 text-armazon-500">{q.numero}</td>
                        <td className="importe px-3 py-1.5 text-armazon-700">
                          {enFecha(q.vencimiento)}
                        </td>
                        <td className="importe px-3 py-1.5 text-right font-semibold">
                          {enDolares(q.valor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-[10px] uppercase leading-relaxed text-armazon-400">
              La mora de cada factura se calcula hasta hoy y se suma a la deuda; las cuotas no
              llevan interés. Si el día de pago no es hábil, la cuota vence el hábil anterior.
            </p>
            <div className="mt-auto flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={alCerrar}
                className="h-10 rounded-lg border border-armazon-200 bg-white px-4 text-xs font-bold uppercase text-armazon-600"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => registrar.mutate()}
                disabled={!valido || registrar.isPending}
                className="h-10 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white disabled:opacity-50"
              >
                Registrar el convenio
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

const CAMPO = 'h-9 w-full rounded-lg border border-armazon-200 px-2 text-xs';

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase text-armazon-500">{etiqueta}</span>
      {children}
    </label>
  );
}

function clave(id: number, historica: boolean): string {
  return `${historica ? 'h' : 'f'}${id}`;
}

function mesSiguiente(): string {
  const hoy = new Date();
  const d = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function mesActual(): string {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}
